import { HttpClient, HttpContext, HttpHeaders, HttpParams } from '@angular/common/http'
import { inject, Injectable, signal } from '@angular/core'
import { BypassErrorHandling, BypassErrorToast } from '@app/v2/shared/helpers/http-context-tokens'
import { QueuedJobModel } from '@app/v2/shared/types/queued-job.model'
import { QueuedJobsResponse, QueuedJobsSocketMessage, QueuedJobsSocketState } from '@app/v2/shared/types/queued-job.type'
import { AuthService } from '@auth/services/auth.service'
import { environment } from '@environments/environment'
import { concat, defer, filter, fromEvent, merge, Observable, of, retry, Subject, Subscription, switchMap, take, throwError, timer } from 'rxjs'
import { webSocket } from 'rxjs/webSocket'

export const QUEUED_JOBS_PAGE_SIZE = 6

@Injectable({
  providedIn: 'root'
})
export class QueuedJobsService {
  private readonly _http = inject(HttpClient)
  private readonly _authService = inject(AuthService)
  private readonly _processingCount = signal<number>(0)
  private readonly _jobsFinished = new Subject<void>()
  private _socketWorkspaceSlug: string | null = null
  private _socketSubscription?: Subscription
  private _activeSocket: QueuedJobsSocketState | null = null

  /** Number of in-progress jobs for the connected workspace, pushed by the Tracked Jobs socket. */
  public readonly processingCount = this._processingCount.asReadonly()
  /** Emits when a socket message reports fewer in-progress jobs than before (not when the socket is disconnected). */
  public readonly jobsFinished$ = this._jobsFinished.asObservable()

  /**
   * Fetches the organization's background jobs (bulk send, import, add from another event).
   * Ordered by the backend: processing jobs first, then most recently updated.
   * @param organizationSlug {string} - Current organization slug.
   * @param page {number} - Page number (1-based).
   * @param perPage {number} - Page size.
   * @returns Observable with the paginated queued jobs.
   */
  public getQueuedJobs(organizationSlug: string, page = 1, perPage = QUEUED_JOBS_PAGE_SIZE): Observable<QueuedJobsResponse> {
    const params = new HttpParams().set('page', page).set('per_page', perPage)

    return this._http.get<QueuedJobsResponse>(`${environment.registrationsApi}organizations/${organizationSlug}/tracked_jobs`, {
      params,
      headers: new HttpHeaders({ 'organization-slug': organizationSlug }),
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Fetches a job report file (e.g. import report CSV) as a blob so it can be saved locally.
   * @param fileUrl {string} - Absolute report URL from the job's `file_url`.
   * @returns Observable with the report file.
   */
  public downloadReport(fileUrl: string): Observable<Blob> {
    return this._http.get(fileUrl, {
      responseType: 'blob',
      context: new HttpContext().set(BypassErrorHandling, true).set(BypassErrorToast, true)
    })
  }

  /**
   * Opens the Tracked Jobs socket for a workspace, replacing any socket for another workspace.
   * The socket stays open while the tab is hidden, reconnects with backoff after any close,
   * and restarts immediately when the tab becomes visible while it is down.
   * @param workspaceSlug {string} - Workspace (organization) slug.
   */
  public connectSocket(workspaceSlug: string): void {
    if (this._socketWorkspaceSlug === workspaceSlug && this._socketSubscription) return

    this.disconnectSocket()
    this._socketWorkspaceSlug = workspaceSlug

    const visibleWhileClosed$ = fromEvent(document, 'visibilitychange').pipe(filter(() => document.visibilityState === 'visible' && !this._activeSocket?.isOpen))

    this._socketSubscription = merge(of(null), visibleWhileClosed$)
      .pipe(
        switchMap(() => this._socketWithReconnect$(workspaceSlug)),
        filter((message): message is QueuedJobsSocketMessage => this._isWorkspaceCountMessage(message, workspaceSlug))
      )
      .subscribe((message) => {
        this._processingCount.set(message.processingCount)
        if (message.processingCount < this._processingCount()) this._jobsFinished.next()
      })
  }

  /**
   * Closes the Tracked Jobs socket without reconnecting (unsubscribing closes it) and resets the count.
   */
  public disconnectSocket(): void {
    this._socketSubscription?.unsubscribe()
    this._socketSubscription = undefined
    this._socketWorkspaceSlug = null
    this._activeSocket = null
    this._processingCount.set(0)
  }

  /**
   * Socket messages with automatic reconnection. Each attempt fetches a token; if the previous attempt closed
   * before opening (e.g. the handshake was rejected with 401), the token is force-refreshed. Backoff resets on any message.
   * @param workspaceSlug {string} - Workspace (organization) slug.
   * @returns Socket messages (`null` for unparseable payloads); never completes.
   */
  private _socketWithReconnect$(workspaceSlug: string): Observable<QueuedJobsSocketMessage | null> {
    let shouldRefreshToken = false

    return defer(() => this._authService.getAccessToken$({ force: shouldRefreshToken })).pipe(
      take(1),
      switchMap((token) => (token ? this._socketUntilClose$(workspaceSlug, token) : throwError(() => new Error('Missing access token')))),
      retry({
        delay: (_error, attempt) => {
          shouldRefreshToken = !this._activeSocket?.hasOpened
          return timer(QueuedJobModel.socketReconnectDelayMs(attempt))
        },
        resetOnSuccess: true
      })
    )
  }

  /**
   * One socket attempt. Errors once the socket closes, so `retry` treats a clean close like a dropped connection.
   * Each attempt tracks its own open state, so a replaced socket's late close event cannot affect the current one.
   * @param workspaceSlug {string} - Workspace (organization) slug.
   * @param token {string} - Access token (without `Bearer`).
   * @returns Socket messages until the socket closes.
   */
  private _socketUntilClose$(workspaceSlug: string, token: string): Observable<QueuedJobsSocketMessage | null> {
    return defer(() => {
      const socketState: QueuedJobsSocketState = { isOpen: false, hasOpened: false }
      this._activeSocket = socketState

      const socket$ = webSocket<QueuedJobsSocketMessage | null>({
        url: this._socketUrl(workspaceSlug, token),
        deserializer: (event) => this._parseSocketMessage(event.data),
        openObserver: {
          next: () => {
            socketState.isOpen = true
            socketState.hasOpened = true
          }
        },
        closeObserver: { next: () => (socketState.isOpen = false) }
      })

      return concat(
        socket$,
        throwError(() => new Error('Tracked jobs socket closed'))
      )
    })
  }

  private _socketUrl(workspaceSlug: string, token: string): string {
    const url = new URL(`${environment.rtsWS}tracked-jobs/${encodeURIComponent(workspaceSlug)}`)
    url.searchParams.set('token', token)
    return url.toString()
  }

  /**
   * Parses a raw socket payload; invalid JSON yields `null` so it is filtered out instead of erroring the stream.
   * @param rawMessage {unknown} - Raw socket payload.
   * @returns Parsed message or `null`.
   */
  private _parseSocketMessage(rawMessage: unknown): QueuedJobsSocketMessage | null {
    try {
      return JSON.parse(String(rawMessage)) as QueuedJobsSocketMessage
    } catch {
      return null
    }
  }

  /**
   * Checks if a socket message is a workspace count message.
   * @param message {QueuedJobsSocketMessage | null} - The socket message.
   * @param workspaceSlug {string} - The workspace slug.
   * @returns Whether the message is a workspace count message.
   */
  private _isWorkspaceCountMessage(message: QueuedJobsSocketMessage | null, workspaceSlug: string): boolean {
    return message?.type === 'data' && message.workspace === workspaceSlug && typeof message.processingCount === 'number'
  }
}
